import KmxVisualSearchStroked from '@kmx/mui-icons/KmxVisualSearchStroked';
import Button, { type ButtonProps } from '@mui/material/Button';
import Stack, { type StackProps } from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { memo, useCallback } from 'react';

import { TaggingEvent } from '@/context/tagging';
import useTagging from '@/hooks/useTagging';

import type { FeaturedCarTileProps } from './FeaturedCarTileProps';

const Wrapper = styled(Stack)<StackProps>(() => ({
  flexWrap: 'wrap',
  marginTop: '1.5rem',

  '[data-orientation="vertical"] &': {
    marginTop: '0.5rem',
    width: '100%',
  },

  '.MuiButton-root': {
    flex: '1 0 max-content',

    '[data-orientation="horizontal"] &': {
      // Buttons will remain at their max-content width.
      '@container (width >= calc(1000px - 1rem))': {
        flex: '0 0 max-content',
      },
    },
  },
}));

type ActionsProps = {
  buttonSize?: ButtonProps['size'];
  detailsHref: string;
  onClick: NonNullable<FeaturedCarTileProps['actions']>['onClick'];
};

function Actions({ buttonSize, detailsHref, onClick }: ActionsProps) {
  const { dispatchTaggingEvent } = useTagging();

  const handleShopSimilarClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickShopSimilarButton,
      data: { text: 'view similar' },
    });
    onClick('similar');
  }, [dispatchTaggingEvent, onClick]);

  const handleDetailsClick = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickDetailsButton,
      data: { text: 'get more details' },
    });
    onClick('details');
  }, [dispatchTaggingEvent, onClick]);

  return (
    <Wrapper direction="row" gap={1}>
      <Button
        data-ignore-anchor-tag-rule
        data-testid="shopsimilar"
        onClick={handleShopSimilarClick}
        size={buttonSize}
        startIcon={<KmxVisualSearchStroked fontSize="inherit" />}
        variant="outlined"
      >
        View similar
      </Button>
      <Button
        data-ignore-anchor-tag-rule
        data-testid="cardetails"
        href={detailsHref}
        onClick={handleDetailsClick}
        size={buttonSize}
        variant="contained"
      >
        Get more details
      </Button>
    </Wrapper>
  );
}

export default memo(Actions);
